import React, { useEffect, useState } from 'react';
import { FiAlertTriangle, FiCheckCircle, FiEye, FiEyeOff, FiMaximize, FiMonitor, FiX, FiXCircle } from 'react-icons/fi';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import API from '../../utils/api';
import styles from './AdminProctoringViolationDetail.module.css';

export default function AdminProctoringViolationDetail() {
  const { candidateId } = useParams();
  const navigate = useNavigate();
  const [violation, setViolation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedViolationType, setSelectedViolationType] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await API.get(`/admin/proctoring-violations/${candidateId}`);
        setViolation(res.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [candidateId]);

  const getViolationIcon = (type) => {
    const icons = {
      tab_switch: FiMonitor,
      window_blur: FiEye,
      visibility_hidden: FiEyeOff,
      browser_close_attempt: FiXCircle,
      fullscreen_exit: FiMaximize
    };
    const Icon = icons[type] || FiAlertTriangle;
    return <Icon aria-hidden="true" />;
  };

  const getViolationLabel = (type) => {
    const labels = {
      tab_switch: 'Tab Switch',
      window_blur: 'Window Lost Focus',
      visibility_hidden: 'Window Hidden',
      browser_close_attempt: 'Browser Close Attempt',
      fullscreen_exit: 'Fullscreen Exit'
    };
    return labels[type] || type;
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      clear: { label: 'Clear', color: 'green' },
      flagged: { label: 'Flagged', color: 'red' }
    };
    return statusMap[status] || { label: status, color: 'gray' };
  };

  // Group violations by type
  const groupedViolations = () => {
    if (!violation.violations) return {};
    const grouped = {};
    violation.violations.forEach(v => {
      if (!grouped[v.type]) {
        grouped[v.type] = [];
      }
      grouped[v.type].push(v);
    });
    return grouped;
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className={styles.page}>
          <div className={styles.loading}>Loading violation details...</div>
        </div>
      </AdminLayout>
    );
  }

  if (!violation) {
    return (
      <AdminLayout>
        <div className={styles.page}>
          <div className={styles.error}>Violation record not found</div>
        </div>
      </AdminLayout>
    );
  }

  const candidate = violation.candidate;
  const statusBadge = getStatusBadge(violation.status);
  const startDate = new Date(violation.assessmentStartedAt).toLocaleDateString('en-GB', { 
    day: '2-digit', 
    month: 'short', 
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  return (
    <AdminLayout>
      <div className={styles.page}>
        <div className={styles.backBtn} onClick={() => navigate('/admin/proctoring-violations')}>
          ← Back to Violations
        </div>

        {/* Candidate Header */}
        <div className={styles.candidateHeader}>
          <div className={styles.candidateAvatar}>{candidate?.fullName?.[0]}</div>
          <div className={styles.candidateInfo}>
            <h2 className={styles.candidateName}>{candidate?.fullName}</h2>
            <div className={styles.candidateMeta}>
              <span className={styles.email}>{candidate?.email}</span>
              <span className={styles.separator}>•</span>
              <span className={styles.role}>{candidate?.role}</span>
            </div>
          </div>
          <span className={`${styles.statusBadge} ${styles[`status${statusBadge.color}`]}`}>
            {statusBadge.label}
          </span>
        </div>

        {/* Summary Stats */}
        <div className={styles.summaryGrid}>
          <div className={styles.summaryCard}>
            <div className={styles.summaryLabel}>Total Violations</div>
            <div className={styles.summaryValue}>{violation.violationCount}</div>
          </div>
          <div className={styles.summaryCard}>
            <div className={styles.summaryLabel}>Assessment Started</div>
            <div className={styles.summaryValue} style={{ fontSize: '0.9rem' }}>{startDate}</div>
          </div>
          <div className={styles.summaryCard}>
            <div className={styles.summaryLabel}>Current Status</div>
            <div className={styles.summaryValue}>{violation.status.replace('_', ' ')}</div>
          </div>
        </div>

        {/* Violations List */}
        {violation.violations && violation.violations.length > 0 ? (
          <div className={styles.section}>
            <div className={styles.sectionTitle}>Detected Violations</div>
            <div className={styles.violationsGrid}>
              {Object.entries(groupedViolations()).map(([type, violations]) => (
                <div 
                  key={type} 
                  className={styles.violationItem}
                  onClick={() => setSelectedViolationType(type)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className={styles.violationIcon}>{getViolationIcon(type)}</div>
                  <div className={styles.violationContent}>
                    <div className={styles.violationType}>{getViolationLabel(type)}</div>
                    <div className={styles.violationDesc}>{violations.length} occurrence{violations.length !== 1 ? 's' : ''}</div>
                    <div className={styles.violationTime}>
                      First: {new Date(violations[0].timestamp).toLocaleTimeString('en-GB')}
                    </div>
                  </div>
                  <div className={styles.violationCount}>{violations.length}</div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className={styles.noViolations}>
            <div className={styles.noViolationsIcon}><FiCheckCircle aria-hidden="true" /></div>
            <div className={styles.noViolationsText}>No violations recorded</div>
          </div>
        )}

        {/* Modal for violation details */}
        {selectedViolationType && (
          <div className={styles.modalOverlay} onClick={() => setSelectedViolationType(null)}>
            <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
              <div className={styles.modalHeader}>
                <div className={styles.modalTitle}>
                  <span className={styles.modalIcon}>{getViolationIcon(selectedViolationType)}</span>
                  {getViolationLabel(selectedViolationType)}
                </div>
                <button className={styles.modalClose} onClick={() => setSelectedViolationType(null)} aria-label="Close"><FiX aria-hidden="true" /></button>
              </div>
              <div className={styles.modalBody}>
                {groupedViolations()[selectedViolationType]?.map((v, idx) => (
                  <div key={idx} className={styles.violationDetailItem}>
                    <div className={styles.violationDetailTime}>
                      {new Date(v.timestamp).toLocaleTimeString('en-GB')}
                    </div>
                    <div className={styles.violationDetailDesc}>{v.description}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Admin Actions */}
        <div className={styles.actions}>
          {violation.assessment ? (
            <button className={styles.actionBtn} onClick={() => {
              const candidateSlug = violation.assessment?.candidate?.slug || violation.assessment?.candidate;
              console.log('Navigating to:', `/admin/results/${candidateSlug}`);
              navigate(`/admin/results/${candidateSlug}`);
            }}>
              View Assessment Result
            </button>
          ) : (
            <div className={styles.noResultMessage}>
              No Assessment Result Yet
            </div>
          )}
          <button className={styles.actionBtn} onClick={() => navigate('/admin/proctoring-violations')}>
            Back to All Violations
          </button>
        </div>
      </div>
    </AdminLayout>
  );
}
